import React, { useEffect, useState } from "react";
import { X, Smartphone, Download } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Shows a dismissible "Install AfriGoall" banner using the
 * beforeinstallprompt event (Chrome/Edge/Android). Falls back to iOS
 * "Add to Home Screen" instructions on Safari.
 * Persists dismissal in localStorage for 14 days.
 */
export default function InstallPWA() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem("afri_pwa_dismissed") || 0);
    if (dismissedAt && Date.now() - dismissedAt < 14 * 24 * 60 * 60 * 1000) return;

    // Detect already installed (standalone / iOS)
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // iOS Safari check
    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    const inSafari = iOS && !/crios|fxios|edgios/.test(ua);
    if (inSafari) {
      setIsIOS(true);
      const t = setTimeout(() => setVisible(true), 4000);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem("afri_pwa_dismissed", String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice?.outcome === "accepted") {
      localStorage.setItem("afri_pwa_installed", "1");
    }
    setPrompt(null);
    setVisible(false);
  };

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 border border-[#e65f2b] bg-black shadow-2xl"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 bg-[#e65f2b] text-black flex items-center justify-center shrink-0">
          <Smartphone size={20} strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display uppercase text-sm font-bold tracking-widest text-white">
            {t("pwa.title")}
          </div>
          {isIOS ? (
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              {t("pwa.iosBody")}
            </p>
          ) : (
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              {t("pwa.body")}
            </p>
          )}
          {!isIOS && (
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="mt-3 inline-flex items-center gap-2 bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest text-xs px-3 py-2"
            >
              <Download size={12} strokeWidth={3} /> {t("pwa.installBtn")}
            </button>
          )}
        </div>
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          className="text-white/40 hover:text-white shrink-0"
          aria-label="Dismiss install banner"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
